import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMediums, sortOthersLast } from "../hooks/queries";

interface MediumOption {
  label: string;
  value: string;
}

interface MediumMultiDropdownProps {
  value: string[];
  onChange: (next: string[]) => void;
}

const MediumMultiDropdown: React.FC<MediumMultiDropdownProps> = ({
  value,
  onChange,
}) => {
  const { data = [] } = useMediums();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // "Other" categories sink to the bottom — same convention as the
  // single-select MediumDropdown2.
  const options: MediumOption[] = useMemo(
    () =>
      sortOthersLast(data, (m: any) => m?.categoryId?.name).map((m: any) => ({
        label: `${m?.name} (${m?.categoryId?.name})`,
        value: m?._id,
      })),
    [data]
  );

  useEffect(() => {
    if (!isOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isOpen]);

  const valueSet = useMemo(() => new Set(value), [value]);
  const allSelected = options.length > 0 && valueSet.size === options.length;
  const someSelected = valueSet.size > 0 && !allSelected;

  const toggleOne = (id: string) => {
    if (valueSet.has(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const toggleAll = () => {
    if (allSelected) onChange([]);
    else onChange(options.map((o) => o.value));
  };

  const triggerLabel = (() => {
    if (valueSet.size === 0) return "All mediums";
    if (valueSet.size === 1) {
      const only = options.find((o) => o.value === value[0]);
      return only?.label || "1 selected";
    }
    return `${valueSet.size} selected`;
  })();

  return (
    <div className="mediumMulti" ref={containerRef}>
      <button
        type="button"
        className="mediumMulti__trigger"
        onClick={() => setIsOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="mediumMulti__triggerLabel">{triggerLabel}</span>
        <i
          className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"}`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div className="mediumMulti__panel" role="listbox">
          <div className="mediumMulti__panelHeader">
            <label className="mediumMulti__row mediumMulti__row--header">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={toggleAll}
              />
              <span>Select all</span>
            </label>
            {valueSet.size > 0 && (
              <button
                type="button"
                className="mediumMulti__clearLink"
                onClick={() => onChange([])}
              >
                Clear
              </button>
            )}
          </div>
          <div className="mediumMulti__panelList">
            {options.map((opt) => {
              const checked = valueSet.has(opt.value);
              return (
                <label
                  key={opt.value}
                  className={`mediumMulti__row ${
                    checked ? "mediumMulti__row--checked" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleOne(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MediumMultiDropdown;
